import { Suspense, useEffect, useRef, useState } from 'react'
import { Bounds, Environment } from '@react-three/drei'
import { motion, useInView } from 'framer-motion'
import CharacterModel from './three/CharacterModel'
import SafeCanvas from './three/SafeCanvas'
import { prefetchModels } from './three/CharacterModel'
import { MODELS, PROFILE, LINKS } from '../config'
// models start downloading the moment this module is parsed — before React
// even mounts — so the hero character arrives with the text, not after it
prefetchModels()

function SocialIcon({ icon }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  }
  if (icon === 'github')
    return (
      <svg {...common} strokeWidth={0} fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.46-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.9 1.53 2.34 1.09 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.56-1.11-4.56-4.95 0-1.09.39-1.99 1.03-2.69-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.03a9.4 9.4 0 0 1 5 0c1.9-1.3 2.75-1.03 2.75-1.03.55 1.38.2 2.4.1 2.65.64.7 1.03 1.6 1.03 2.69 0 3.85-2.34 4.7-4.57 4.95.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10 10 0 0 0 12 2z" />
      </svg>
    )
  if (icon === 'linkedin')
    return (
      <svg {...common}>
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
        <rect x="2" y="9" width="4" height="12" />
        <circle cx="4" cy="4" r="2" />
      </svg>
    )
  if (icon === 'mail')
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 7l9 6 9-6" />
      </svg>
    )
  return null
}

const socials = [
  { label: 'GitHub', href: LINKS.github, icon: 'github' },
  { label: 'LinkedIn', href: LINKS.linkedin, icon: 'linkedin' },
  { label: 'Email', href: LINKS.email, icon: 'mail' },
]

export default function Hero() {
  const sectionRef = useRef(null)
  // Pause the WebGL render loop once the hero is far off-screen
  const inView = useInView(sectionRef, { margin: '120% 0px 120% 0px' })
  const [isMobile, setIsMobile] = useState(false)
  // camera auto-fit mounts only after the model exists — a late/failed
  // load can no longer break the framing into an invisible pose
  const [modelReady, setModelReady] = useState(false)
  const [modelFailed, setModelFailed] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden"
    >
      {/* 1. split background */}
      <div className="absolute inset-0 z-0 flex">
        <div className="h-full w-[60%] bg-[var(--hero-dark)] md:w-1/2" />
        <div className="h-full w-[40%] bg-[var(--hero-orange)] md:w-1/2" />
      </div>

      {/* 2. the character — streams in with zero gating, landing plays the moment it arrives */}
      <div className="absolute inset-y-0 right-0 z-10 w-[85%] md:w-1/2">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="h-full w-full"
        >
          <SafeCanvas
            canvasProps={{
              dpr: [1, 1.75],
              camera: { position: [0, 0, 5], fov: 45 },
              frameloop: inView ? 'always' : 'never',
              gl: { alpha: true, antialias: true },
            }}
          >
            <Suspense fallback={null}>
              <ambientLight intensity={0.5} />
              <directionalLight position={[10, 10, 5]} intensity={1.5} />
              <directionalLight position={[-10, -10, -5]} intensity={0.5} />
              <Suspense fallback={null}>
                {/* own suspense: a slow/failed HDRI must never hold the model hostage */}
                <Environment files={MODELS.hdr} />
              </Suspense>
              {modelReady ? (
                <Bounds fit clip observe margin={isMobile ? 0.6 : 1.2}>
                  <CharacterModel withLanding headTracking />
                </Bounds>
              ) : modelFailed ? (
                <div className="flex h-full w-full items-center justify-center">
                  <div className="flex h-44 w-44 items-center justify-center rounded-full border-2 border-white/25 bg-black/40">
                    <span className="font-display text-4xl font-semibold text-white/85">VK</span>
                  </div>
                </div>
              ) : (
                <CharacterModel
                  withLanding
                  headTracking={false}
                  onReady={() => setModelReady(true)}
                  onMissing={() => setModelFailed(true)}
                />
              )}
            </Suspense>
          </SafeCanvas>
        </motion.div>
        <span className="font-mono absolute bottom-6 right-6 z-20 text-xs text-[var(--hero-dark)]/60 md:bottom-10 md:right-10">
          {new Date().getFullYear()}
        </span>
      </div>

      {/* 3. type over the character — visible instantly, never behind a loader */}
      <div className="pointer-events-none relative z-20 flex h-full w-full flex-col justify-center px-6 py-24 text-[var(--hero-ivory)] md:pointer-events-auto md:w-1/2 md:px-14 lg:px-20">
        <div className="pointer-events-auto max-w-[300px] sm:max-w-sm md:max-w-none">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--hero-orange)]"
          >
            {PROFILE.location}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.06 }}
            className="font-display mt-5 text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl"
          >
            Hi there, I'm
            <br />
            <span className="text-white">{PROFILE.name}</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="font-display mt-3 flex flex-wrap gap-x-3 gap-y-1 text-lg font-medium text-white/35 sm:text-xl"
          >
            {PROFILE.roleLine.map((r, i) => (
              <span key={r} className={i === 0 ? 'text-white/70' : ''}>
                {r}
                {i < PROFILE.roleLine.length - 1 && <span className="ml-3 text-white/20">/</span>}
              </span>
            ))}
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18 }}
            className="mt-6 text-sm leading-relaxed text-white/60 md:max-w-md md:text-base md:[text-shadow:none] [text-shadow:_0_1px_14px_rgba(13,13,13,0.9)]"
          >
            {PROFILE.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.24 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#experience"
              data-testid="hero-cta-journey"
              className="font-display rounded-md bg-[var(--hero-orange)] px-6 py-3 text-sm font-semibold text-[#0d0d0d] transition hover:brightness-110"
            >
              Explore my journey
            </a>
            <a
              href="#projects"
              data-testid="hero-cta-work"
              className="font-display rounded-md border border-white/25 px-6 py-3 text-sm font-semibold text-white/85 transition hover:border-white/60"
            >
              See what I've built
            </a>
          </motion.div>

          <div className="mt-12 flex items-center gap-5 text-white/40 md:absolute md:bottom-12 md:left-14 md:mt-0 lg:left-20">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target={s.href.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                aria-label={s.label}
                data-testid={`hero-social-${s.icon}`}
                className="flex items-center justify-center transition hover:text-[var(--hero-orange)]"
              >
                <SocialIcon icon={s.icon} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
